import { ThemeProvider } from "@mui/material";
import { prefixClassName } from "@smartrr/shared/components/primitives";
import { AquariusCard } from "@smartrr/shared/customerPortal/components/AquariusCard";
import { DotSpinner } from "@smartrr/shared/customerPortal/components/DotSpinner";
import { FailureRes } from "@smartrr/shared/req";
import { captureException } from "@smartrr/shared/utils/captureException";
import React, { useEffect, useRef, useState } from "react";

import { MagicLinkSuccess } from "../../libs/components/FormSteps/MagicLinkSuccess";
import PasswordlessLoginForm from "../../libs/components/FormSteps/PasswordlessLoginForm";
import { ReturnFromEmail } from "../../libs/components/FormSteps/ReturnFromEmail";
import { useThemeOverrides } from "../../libs/hooks/useThemeOverrides";
import { AuthFlowContainerStyles } from "../../libs/styles";
import { LoginError, PASSWORDLESS_LOGIN_STEPS, QUERY_PARAMS, REQUEST_STATUS } from "../../libs/types";
import { validateEmailAddressFormat } from "../../libs/utils";
import { sendMagicLink, verifyMagicLink } from "../../libs/utils/magicLinkAPI";
import "../../../account/fonts.css";
import { transformErrorResponse } from "../../libs/utils/transformErrorResponse";

export default function MagicLinkFlow({ shopifyDomain }: { shopifyDomain: string | null }): JSX.Element {
  const theme = useThemeOverrides();

  const [formStep, setFormStep] = useState<PASSWORDLESS_LOGIN_STEPS>(PASSWORDLESS_LOGIN_STEPS.LOGIN_FORM);

  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");

  const [isLoading, setIsLoading] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState<boolean | undefined>(undefined);

  const [loginError, setLoginError] = useState<LoginError | undefined>(undefined);

  const hiddenFormSubmitBtnRef = useRef<HTMLButtonElement | null>(null);

  /**
   * @description This useEffect powers the behavior when a user returns to the application after clicking on a magic link in their email.
   * It leverages the CID query param to get the users auth credentials, sets the application to the proper state and logs them in if successful.
   * If unsuccessful it sets the application to the initial state and informs the user by displaying the relevant error/information
   */
  useEffect(() => {
    const params = new URL(document.location as unknown as string).searchParams;
    const magicLinkCID = params.get(QUERY_PARAMS.CID);
    if (magicLinkCID) {
      async function fetchMagicLinkCredentials() {
        try {
          setFormStep(PASSWORDLESS_LOGIN_STEPS.EMAIL_CALLBACK);
          const res = await verifyMagicLink(magicLinkCID!);
          if (res.type === REQUEST_STATUS.SUCCESS) {
            const { email, confirmationHash } = res;
            setEmail(email!);
            setPassword(atob(confirmationHash!));
            // Note: Submitting this form causes a refresh as it is really submitting the form this is loaded into within the shopify login in form.
            setTimeout(() => {
              hiddenFormSubmitBtnRef.current?.click();
            }, 500);
          } else {
            setFormStep(PASSWORDLESS_LOGIN_STEPS.LOGIN_FORM);
            setLoginError(transformErrorResponse(res as FailureRes));
            captureException("Unable to fetch users credentials using cid", res.message);
          }
        } catch (error) {
          setFormStep(PASSWORDLESS_LOGIN_STEPS.LOGIN_FORM);
          setIsLoading(false);
          setLoginError(transformErrorResponse(error as FailureRes));
          captureException("Magic Link Verify Error", error);
        }
      }
      fetchMagicLinkCredentials();
    }
  }, []);

  const handleEmailInputChange = (email: string) => {
    setEmail(email.trim().toLocaleLowerCase());
    setIsEmailValid(validateEmailAddressFormat(email));
  };

  const handleEmailInputBlur = (email: string) => {
    setIsEmailValid(validateEmailAddressFormat(email));
  };

  const requestMagicLink = async (email: string, shopifyDomain: string) => {
    setIsLoading(true);
    try {
      const res = await sendMagicLink({ email, shopifyDomain });
      if (res.type === REQUEST_STATUS.SUCCESS) {
        setLoginError(undefined);
        setIsLoading(false);
        setFormStep(PASSWORDLESS_LOGIN_STEPS.LOGIN_SUCCESS);
      } else {
        setLoginError(transformErrorResponse(res as FailureRes));
        setIsLoading(false);
        captureException("Unable to send magic link", res.message);
      }
    } catch (error) {
      setIsLoading(false);
      setLoginError(transformErrorResponse(error as FailureRes));
      captureException("Send Magic Link Request Error", error);
    }
  };

  const renderFormSteps = (formStep: PASSWORDLESS_LOGIN_STEPS) => {
    switch (formStep) {
      case PASSWORDLESS_LOGIN_STEPS.LOGIN_FORM: {
        return (
          <PasswordlessLoginForm
            email={email}
            theme={theme}
            isEmailValid={isEmailValid}
            shopifyDomain={shopifyDomain!}
            loginError={loginError}
            requestMagicLink={() => requestMagicLink(email, shopifyDomain!)}
            handleEmailInputBlur={handleEmailInputBlur}
            handleEmailInputChange={handleEmailInputChange}
          />
        );
      }
      case PASSWORDLESS_LOGIN_STEPS.LOGIN_SUCCESS: {
        return <MagicLinkSuccess resendLink={() => requestMagicLink(email, shopifyDomain!)} />;
      }
      case PASSWORDLESS_LOGIN_STEPS.EMAIL_CALLBACK: {
        return (
          <ReturnFromEmail email={email} password={password} hiddenFormSubmitBtnRef={hiddenFormSubmitBtnRef} />
        );
      }
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <AquariusCard
        data-testid="magic-link-wrapper"
        className={prefixClassName("magic-link-wrapper")}
        sx={AuthFlowContainerStyles}
      >
        {isLoading ? <DotSpinner /> : renderFormSteps(formStep)}
      </AquariusCard>
    </ThemeProvider>
  );
}
